import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Grid,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { PageHeader } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Create / edit a Sales Opportunity as a dedicated full page (not a modal),
// mirroring QuotationFormPage / SalesContractFormPage: titled sections and a
// sticky bottom action bar. The create/update apiClient calls and payload
// shape are unchanged from the old dialog, so the backend is untouched.
// ---------------------------------------------------------------------------

const STAGES = [
  { value: 'PROSPECTING', label: 'Prospecting' },
  { value: 'QUALIFICATION', label: 'Qualification' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
];

interface Customer {
  id: number;
  company_name: string;
  contact_person: string;
}

interface OpportunityFormData {
  opportunityName: string;
  customerId: number | null;
  estimatedValue: number;
  probability: number;
  expectedClosureDate: string;
  stage: string;
  notes: string;
}

interface OpportunityFromApi {
  id: number;
  customerId?: number;
  opportunityName: string;
  estimatedValue: number;
  probability: number;
  expectedClosureDate?: string;
  stage: string;
  notes?: string;
}

const emptyForm = (): OpportunityFormData => ({
  opportunityName: '',
  customerId: null,
  estimatedValue: 0,
  probability: 0,
  expectedClosureDate: '',
  stage: 'PROSPECTING',
  notes: '',
});

const OpportunityFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const c = useControlRoomColors();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingOpportunity, setLoadingOpportunity] = useState(isEdit);

  const [formData, setFormData] = useState<OpportunityFormData>(emptyForm());

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    api
      .get('/sales/customers')
      .then((data: any) => setCustomers(data.customers || []))
      .catch((err: any) => console.error('Error fetching customers:', err))
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingOpportunity(true);
    api
      .get<{ opportunity: OpportunityFromApi }>(`/sales/opportunities/${id}`)
      .then(({ opportunity }) => {
        setFormData({
          opportunityName: opportunity.opportunityName,
          customerId: opportunity.customerId || null,
          estimatedValue: opportunity.estimatedValue,
          probability: opportunity.probability,
          expectedClosureDate: opportunity.expectedClosureDate ? opportunity.expectedClosureDate.split('T')[0] : '',
          stage: opportunity.stage,
          notes: opportunity.notes || '',
        });
      })
      .catch((err: any) => setError(err.message || 'Failed to load opportunity'))
      .finally(() => setLoadingOpportunity(false));
  }, [isEdit, id]);

  const handleFormChange = (field: keyof OpportunityFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!formData.opportunityName.trim()) errs.push('Opportunity name is required.');
    if (formData.probability < 0 || formData.probability > 100) errs.push('Probability must be between 0 and 100.');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        await api.put(`/sales/opportunities/${id}`, formData);
      } else {
        await api.post('/sales/opportunities', formData);
      }
      navigate('/sales/opportunities');
    } catch (err: any) {
      setError(err.message || 'Failed to save opportunity');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingOpportunity;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/opportunities')} sx={{ mb: 1 }}>
        Back to Opportunities
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Opportunity' : 'New Opportunity'}
        subtitle="Track a sales lead through the pipeline, from first contact to close."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      {/* Section: Basic */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Basic
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Opportunity Name *"
              value={formData.opportunityName}
              onChange={(e) => handleFormChange('opportunityName', e.target.value)}
              required
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Customer & Contact */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Customer &amp; Contact
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              size="small"
              options={customers}
              getOptionLabel={(option) => option.company_name || ''}
              value={customers.find(cust => cust.id === formData.customerId) || null}
              onChange={(_, newValue) => handleFormChange('customerId', newValue?.id || null)}
              renderInput={(params) => (
                <TextField {...params} label="Customer" placeholder="Select customer" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Value & Stage */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Value &amp; Stage
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Estimated Value"
              type="number"
              value={formData.estimatedValue}
              onChange={(e) => handleFormChange('estimatedValue', parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Stage *</InputLabel>
              <Select
                value={formData.stage}
                onChange={(e) => handleFormChange('stage', e.target.value)}
                label="Stage *"
              >
                {STAGES.map(stage => (
                  <MenuItem key={stage.value} value={stage.value}>{stage.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Forecast */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Forecast
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Probability (%)"
              type="number"
              value={formData.probability}
              onChange={(e) => {
                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                handleFormChange('probability', val);
              }}
              inputProps={{ min: 0, max: 100 }}
              helperText="0-100%"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Expected Close Date"
              type="date"
              value={formData.expectedClosureDate}
              onChange={(e) => handleFormChange('expectedClosureDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Notes */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Notes
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          label="Notes"
          value={formData.notes}
          onChange={(e) => handleFormChange('notes', e.target.value)}
        />
      </Paper>

      {/* Sticky bottom action bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 1,
          py: 1.5,
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: c.panel,
        }}
      >
        <Button onClick={() => navigate('/sales/opportunities')} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          disabled={submitting || !formData.opportunityName}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Opportunity'}
        </Button>
      </Paper>
    </Box>
  );
};

export default OpportunityFormPage;
